import {
  isDocRef,
  isTimestampRange,
  Timestamp,
  TypeGuard,
  type DocumentReference,
  type ISoftDelete,
  type ITimestampRange,
} from '@principle-theorem/shared';
import { isString } from 'lodash';
import { type IBrand } from './brand';
import { type IOrganisation } from './organisation/organisation';
import { type IPractice } from './practice/practice';
import {
  type IPracticeMigration,
  type ISourceEntity,
  type IDestinationEntity,
} from '@principle-theorem/practice-migrations/interfaces';

export enum ManagementJobCollection {
  ManagementJobLogs = 'managementJobLogs',
}

export enum BigQueryManagementJob {
  BigQuerySchemaSync = 'big-query.schema.sync',
  BigQueryAccountCreditsSync = 'big-query.account-credits.sync',
  BigQueryAppointmentsSync = 'big-query.appointments.sync',
  BigQueryInvoicesSync = 'big-query.invoices.sync',
  BigQueryPatientsSync = 'big-query.patients.sync',
  BigQueryPatientInteractionsSync = 'big-query.patient-interactions.sync',
  BigQueryTransactionsSync = 'big-query.transactions.sync',
  BigQueryTreatmentStepSync = 'big-query.treatment-steps.sync',
  BigQuerySchedulingEventSync = 'big-query.scheduling-events.sync',
  BigQuerySchedulingEventReasonSync = 'big-query.scheduling-event-reasons.sync',
}

export const PRACTICE_MIGRATION_JOB_PREFIX = 'practice-migration';

export enum PracticeMigrationManagementJobType {
  AddMigrationDatabase = `${PRACTICE_MIGRATION_JOB_PREFIX}.add-migration-database`,
  DestinationMigrationRun = `${PRACTICE_MIGRATION_JOB_PREFIX}.destination.run-migration`,
  ExportWorkspaceToStagingBucket = `${PRACTICE_MIGRATION_JOB_PREFIX}.export-workspace-to-staging`,
  ImportWorkspaceFromStagingBucket = `${PRACTICE_MIGRATION_JOB_PREFIX}.import-workspace-from-staging`,
  MergeConflictDestinationRecords = `${PRACTICE_MIGRATION_JOB_PREFIX}.destination.merge-conflict-records`,
  PostMigrationDeduplicateAppointmentInteractions = `${PRACTICE_MIGRATION_JOB_PREFIX}.post-migration.deduplicate-appointment-interactions`,
  PostMigrationSeedAccountAggregates = `${PRACTICE_MIGRATION_JOB_PREFIX}.post-migration.seed-account-aggregates`,
  PostMigrationSeedAutomations = `${PRACTICE_MIGRATION_JOB_PREFIX}.post-migration.seed-automations`,
  PostMigrationSeedBigQuery = `${PRACTICE_MIGRATION_JOB_PREFIX}.post-migration.seed-bigquery`,
  PostMigrationSeedPatientMetadata = `${PRACTICE_MIGRATION_JOB_PREFIX}.post-migration.seed-patient-metadata`,
  PostMigrationSeedPatientNumbers = `${PRACTICE_MIGRATION_JOB_PREFIX}.post-migration.seed-patient-numbers`,
  PostMigrationSeedTypesensePatients = `${PRACTICE_MIGRATION_JOB_PREFIX}.post-migration.seed-typesense-patients`,
  PostMigrationUpsertScheduleSummary = `${PRACTICE_MIGRATION_JOB_PREFIX}.post-migration.upsert-schedule-summary`,
  ProvisionMigrationDatabase = `${PRACTICE_MIGRATION_JOB_PREFIX}.provision-migration-database`,
  ProvisionMigrationServiceAccount = `${PRACTICE_MIGRATION_JOB_PREFIX}.provision-migration-service-account`,
  ProvisionStorageBucket = `${PRACTICE_MIGRATION_JOB_PREFIX}.provision-storage-bucket`,
  SyncDatabaseBackup = `${PRACTICE_MIGRATION_JOB_PREFIX}.destination.sync-database-backup`,
  SyncExpectedSourceRecordSizes = `${PRACTICE_MIGRATION_JOB_PREFIX}.source.sync-expected-record-sizes`,
  SyncSourceMigrationRun = `${PRACTICE_MIGRATION_JOB_PREFIX}.source.sync-source-migration`,
  SyncSourceRecordSizes = `${PRACTICE_MIGRATION_JOB_PREFIX}.source.sync-record-sizes`,
  TranslateSeedingFiles = `${PRACTICE_MIGRATION_JOB_PREFIX}.translate-seeding-files`,
  UploadBackupToDatabase = `${PRACTICE_MIGRATION_JOB_PREFIX}.upload-backup-to-database`,
}

export enum DataMigrationJobType {
  BackfillBigQueryDataset = 'data-migration.backfill-bigquery-dataset',
}

export interface IDataMigrationJob {
  type: DataMigrationJobType;
}

export const SOURCE_MIGRATION_JOB_TYPES = [
  PracticeMigrationManagementJobType.TranslateSeedingFiles,
  PracticeMigrationManagementJobType.AddMigrationDatabase,
  PracticeMigrationManagementJobType.ProvisionMigrationDatabase,
  PracticeMigrationManagementJobType.ProvisionMigrationServiceAccount,
  PracticeMigrationManagementJobType.ProvisionStorageBucket,
  PracticeMigrationManagementJobType.UploadBackupToDatabase,
  PracticeMigrationManagementJobType.SyncExpectedSourceRecordSizes,
  PracticeMigrationManagementJobType.SyncSourceRecordSizes,
  PracticeMigrationManagementJobType.SyncSourceMigrationRun,
  PracticeMigrationManagementJobType.SyncDatabaseBackup,
  PracticeMigrationManagementJobType.ExportWorkspaceToStagingBucket,
];

export const DESTINATION_MIGRATION_JOB_TYPES = [
  PracticeMigrationManagementJobType.DestinationMigrationRun,
  PracticeMigrationManagementJobType.PostMigrationDeduplicateAppointmentInteractions,
  PracticeMigrationManagementJobType.PostMigrationSeedPatientNumbers,
  PracticeMigrationManagementJobType.PostMigrationSeedAutomations,
  PracticeMigrationManagementJobType.PostMigrationUpsertScheduleSummary,
  PracticeMigrationManagementJobType.PostMigrationSeedTypesensePatients,
  PracticeMigrationManagementJobType.PostMigrationSeedAccountAggregates,
  PracticeMigrationManagementJobType.PostMigrationSeedPatientMetadata,
  PracticeMigrationManagementJobType.PostMigrationSeedBigQuery,
  PracticeMigrationManagementJobType.MergeConflictDestinationRecords,
];

export enum ManagementJobStatus {
  Pending = 'Pending',
  InProgress = 'In Progress',
  Complete = 'Complete',
  Failed = 'Failed',
  Cancelled = 'Cancelled',
}

export const MANAGEMENT_JOB_STATUS_MAP: {
  [key in ManagementJobStatus]: string;
} = {
  [ManagementJobStatus.Pending]: 'default',
  [ManagementJobStatus.InProgress]: 'accent',
  [ManagementJobStatus.Complete]: 'primary',
  [ManagementJobStatus.Failed]: 'warn',
  [ManagementJobStatus.Cancelled]: 'default',
};

export interface IBigQueryJob {
  range: ITimestampRange;
  organisationRef: DocumentReference<IOrganisation>;
  brandRef: DocumentReference<IBrand>;
  practiceRef: DocumentReference<IPractice>;
  type: BigQueryManagementJob;
}

export interface IPracticeMigrationTaskQueueData {
  managementJobRef: DocumentReference<IManagementJob>;
}

export interface IPracticeMigrationManagementJob {
  migrationRef: DocumentReference<IPracticeMigration>;
  type: PracticeMigrationManagementJobType;
  cloudTaskId?: string;
  continueToNextJob?: boolean;
}

export interface IPracticeMigrationJobSourceEntityBaseState {
  key: string;
  status: 'pending' | 'inProgress' | 'complete' | 'failed';
  ref: DocumentReference<ISourceEntity>;
}

export interface IPracticeMigrationJobDestinationEntityBaseState {
  key: string;
  status: 'pending' | 'inProgress' | 'complete' | 'failed';
  type:
    | 'runAll'
    | 'retryMigrated'
    | 'skipMigrated'
    | 'retryFailed'
    | 'migrateMergeConflicts';
  ref: DocumentReference<IDestinationEntity>;
  recordUid?: string | number;
  lastRecordsAffected?: string;
  runsWithSameRecordCount?: number;
  skipMergeConflictCheck?: boolean;
  migrateMergeConflicts?: boolean;
}

export type IPostMigrationJob = PracticeMigrationManagementJob & {
  type:
    | PracticeMigrationManagementJobType.PostMigrationDeduplicateAppointmentInteractions
    | PracticeMigrationManagementJobType.PostMigrationSeedPatientNumbers
    | PracticeMigrationManagementJobType.PostMigrationSeedAutomations
    | PracticeMigrationManagementJobType.PostMigrationUpsertScheduleSummary
    | PracticeMigrationManagementJobType.PostMigrationSeedTypesensePatients
    | PracticeMigrationManagementJobType.PostMigrationSeedPatientMetadata
    | PracticeMigrationManagementJobType.PostMigrationSeedBigQuery;
};

export type PracticeMigrationSourceHandlerJob =
  IPracticeMigrationManagementJob & {
    type: PracticeMigrationManagementJobType.SyncSourceMigrationRun;
  };

export type PracticeMigrationDatabaseAddJob = PracticeMigrationManagementJob & {
  type: PracticeMigrationManagementJobType.AddMigrationDatabase;
};

export type PracticeMigrationDatabaseProvisionJob =
  PracticeMigrationManagementJob & {
    type: PracticeMigrationManagementJobType.ProvisionMigrationDatabase;
    provisionState: 'pending' | 'inProgress' | 'complete' | 'failed';
  };

export type PracticeMigrationProvisionStorageBucketJob =
  PracticeMigrationManagementJob & {
    type: PracticeMigrationManagementJobType.ProvisionStorageBucket;
  };

export type PracticeMigrationProvisionServiceAccountJob =
  PracticeMigrationManagementJob & {
    type: PracticeMigrationManagementJobType.ProvisionMigrationServiceAccount;
  };

export type PracticeMigrationUploadBackupToDatabaseJob =
  PracticeMigrationManagementJob & {
    type: PracticeMigrationManagementJobType.UploadBackupToDatabase;
    filePath: string;
    bucketName?: string;
  };

export type PracticeMigrationExportWorkspaceJob =
  PracticeMigrationManagementJob & {
    type: PracticeMigrationManagementJobType.ExportWorkspaceToStagingBucket;
  };

export type PracticeMigrationImportWorkspaceJob =
  PracticeMigrationManagementJob & {
    type: PracticeMigrationManagementJobType.ImportWorkspaceFromStagingBucket;
    manifestPath: string; // gs:// path to manifest
  };

export interface ISyncRecordSizeJobSourceEntityState
  extends IPracticeMigrationJobSourceEntityBaseState {
  syncCountAttempts: number;
}

export type PracticeMigrationSyncRecordSizeJob =
  PracticeMigrationManagementJob & {
    type: PracticeMigrationManagementJobType.SyncSourceRecordSizes;
    sourceEntities: ISyncRecordSizeJobSourceEntityState[];
  };

export type PracticeMigrationSyncExpectedRecordSizeJob =
  PracticeMigrationManagementJob & {
    type: PracticeMigrationManagementJobType.SyncExpectedSourceRecordSizes;
    sourceEntities: IPracticeMigrationJobSourceEntityBaseState[];
  };

export interface ISyncSourceRecordsJobSourceEntityState
  extends IPracticeMigrationJobSourceEntityBaseState {
  queryOffset: number;
}

export type PracticeMigrationSyncSourceRecordsJob =
  PracticeMigrationManagementJob & {
    type: PracticeMigrationManagementJobType.SyncSourceMigrationRun;
    sourceEntities: ISyncSourceRecordsJobSourceEntityState[];
    skipMigrated: boolean;
    skipAfter?: Timestamp;
  };

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface IDestinationMigrationJobState
  extends IPracticeMigrationJobDestinationEntityBaseState {}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface IDestinationMigrationMergeConflictJobState
  extends IDestinationMigrationJobState {}

export type PracticeMigrationDestinationMigrateJob =
  PracticeMigrationManagementJob & {
    type: PracticeMigrationManagementJobType.DestinationMigrationRun;
    destinationEntities: IDestinationMigrationJobState[];
  };

export type PracticeMigrationDestinationMergeConflictJob =
  PracticeMigrationManagementJob & {
    migrateMergeConflicts: boolean;
    destinationEntities: IDestinationMigrationMergeConflictJobState[];
  };

export type ManagemetJobType =
  | IBigQueryJob
  | IPracticeMigrationManagementJob
  | IDataMigrationJob;
export type BaseManagementJob = ISoftDelete & {
  status: ManagementJobStatus;
};

export type IManagementJob = BaseManagementJob &
  (IBigQueryJob | IPracticeMigrationManagementJob | IDataMigrationJob);

export type IBigQueryManagementJob = BaseManagementJob & IBigQueryJob;

export type IDataMigrationManagementJob = BaseManagementJob &
  IDataMigrationJob;

export type PracticeMigrationManagementJob = BaseManagementJob &
  IPracticeMigrationManagementJob;

export const isBigQueryJob = TypeGuard.interface<IBigQueryJob>({
  range: isTimestampRange,
  organisationRef: isDocRef,
  brandRef: isDocRef,
  practiceRef: isDocRef,
  type: isString,
});
